import React from 'react';
import styled from '@emotion/styled';
import { theme, SectionWrapper as SectionWrapperStyles } from '@styles';
import { ClubHeroSectionData } from './types';
import { MOUNTAIN_MEADOW, OUTER_SPACE } from '@constants';
import Button from '@components/Button';
import AppleStore from '@components/AppleStore';
import GoogleStore from '@components/GoogleStore';

const { breakpoints } = theme;

const ClubHeroSection: React.FC<ClubHeroSectionData> = (props) => {
  const {
    header_title,
    header_subtitle,
    app_screenshot,
    ios_app_link,
    android_app_link,
    ios_app_id,
    android_app_id,
    b2b_demo_anchor = '#b2b-section'
  } = props;

  // Allow component to render even if some data is missing - individual elements are conditionally rendered

  // Safe data extraction following existing patterns
  const titleText = header_title?.text || '';
  const subtitleText = header_subtitle?.text || '';
  const imageUrl = app_screenshot?.url;
  const imageAlt = app_screenshot?.alt || 'Club App 2.0 screenshot';

  return (
    <SectionWrapper id='clubHero'>
      <ContentWrapper>
        <TextContent>
          {titleText && (
            <HeroTitle>{titleText}</HeroTitle>
          )}
          {subtitleText && (
            <HeroSubtitle>{subtitleText}</HeroSubtitle>
          )}

          <CTAWrapper>
            <StoreButtons>
              {ios_app_link && (
                <AppStoreWrapper>
                  <AppleStore url={ios_app_link} id={ios_app_id} />
                </AppStoreWrapper>
              )}
              {android_app_link && (
                <GoogleStoreWrapper>
                  <GoogleStore url={android_app_link} id={android_app_id} />
                </GoogleStoreWrapper>
              )}
            </StoreButtons>

            <B2BButtonWrapper>
              <Button
                label="Launch Your App"
                type="secondary"
                height={53}
                width={200}
                onClick={() => {
                  const element = document.querySelector(b2b_demo_anchor);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              />
            </B2BButtonWrapper>
          </CTAWrapper>
        </TextContent>

        <VisualContent>
          {imageUrl && (
            <PhoneMockup>
              <img src={imageUrl} alt={imageAlt} />
            </PhoneMockup>
          )}
        </VisualContent>
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default ClubHeroSection;

const SectionWrapper = styled(SectionWrapperStyles)`
  padding: 80px 24px;
  background: linear-gradient(135deg, #f8fafa 0%, #ffffff 100%);
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    padding: 120px 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  gap: 60px;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    flex-direction: row;
    gap: 80px;
    align-items: center;
  }
`;

const TextContent = styled.div`
  flex: 1;
  text-align: center;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    text-align: left;
  }
`;

const HeroTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  line-height: 44px;
  color: ${OUTER_SPACE};
  margin-bottom: 20px;
  font-family: 'Poppins', sans-serif;
  
  em {
    font-style: normal;
    position: relative;
    z-index: 1;
    
    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 4px;
      width: 100%;
      height: 8px;
      background: ${MOUNTAIN_MEADOW};
      z-index: -1;
      opacity: 0.3;
    }
  }
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 48px;
    line-height: 56px;
    
    em:after {
      bottom: 8px;
      height: 12px;
    }
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  line-height: 26px;
  color: ${OUTER_SPACE};
  opacity: 0.8;
  margin-bottom: 40px;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 20px;
    line-height: 28px;
    margin-bottom: 48px;
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    flex-direction: row;
    align-items: center;
    gap: 48px;
  }
`;

const StoreButtons = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  max-width: 340px;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    justify-content: flex-start;
  }
`;

const AppStoreWrapper = styled.div`
  flex: 1;
  height: 44px;
  
  a {
    display: block;
    width: 100%;
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const GoogleStoreWrapper = styled.div`
  flex: 1;
  height: 44px;
  
  a {
    display: block;
    width: 100%;
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const B2BButtonWrapper = styled.div`
  a {
    text-decoration: none;
  }
`;

const VisualContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PhoneMockup = styled.div`
  max-width: 300px;
  width: 100%;
  
  img {
    width: 100%;
    height: auto;
    filter: drop-shadow(0px 20px 40px rgba(0, 0, 0, 0.1));
  }
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    max-width: 400px;
  }
`; 